// frontend/src/pages/admin/AdminIntegrationsPage.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { MessageSquare, Mail, Server, Info } from "lucide-react";

export default function AdminIntegrationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { artist, isDemoMode, platformCapabilities } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    twilioAccountSid: "",
    twilioAuthToken: "",
    twilioPhone: "",
    twilioWhatsapp: "",
    emailProvider: "resend",
    resendApiKey: "",
    smtpHost: "",
    smtpPort: "",
    smtpUser: "",
    smtpPassword: "",
    smtpFrom: "",
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  const [showTwilioInfo, setShowTwilioInfo] = useState(false);
  const [showSmtpInfo, setShowSmtpInfo] = useState(false);

  // Load existing configurations
  const { data: integrations } = useQuery({
    queryKey: ["integrations", artist?.id],
    queryFn: () => api.getIntegrations(artist!.id),
    enabled: !!artist,
  });

  useEffect(() => {
    if (integrations) {
      setFormData({
        twilioAccountSid: integrations.twilioAccountSid || "",
        twilioAuthToken: integrations.twilioAuthToken || "",
        twilioPhone: integrations.twilioPhone || "",
        twilioWhatsapp: integrations.twilioWhatsapp || "",
        emailProvider: integrations.emailProvider || "resend",
        resendApiKey: integrations.resendApiKey || "",
        smtpHost: integrations.smtpHost || "",
        smtpPort: integrations.smtpPort ? integrations.smtpPort.toString() : "",
        smtpUser: integrations.smtpUser || "",
        smtpPassword: integrations.smtpPassword || "",
        smtpFrom: integrations.smtpFrom || "",
      });
    }
  }, [integrations]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.updateIntegrations(artist!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      setModalState({
        isOpen: true,
        title: t("common.success"),
        message: t("admin.integrations.success"),
      });
    },
    onError: (error: any) => {
      setModalState({
        isOpen: true,
        title: t("common.error") || "Error",
        message: error.response?.data?.error || error.message || "Error saving configuration",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!artist) return;
    
    // Parse port as integer
    const payload = {
      ...formData,
      smtpPort: formData.smtpPort ? parseInt(formData.smtpPort) : null,
    };

    updateMutation.mutate(payload);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="p-5 sm:p-8 sm:pb-4 border-b border-gray-100">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="text-sky-700 hover:text-sky-800 font-medium mb-6 flex items-center gap-2 transition-colors"
            >
              ← {t("common.backToDashboard") || "Back to Dashboard"}
            </button>
            <h1 className="text-xl sm:text-3xl font-black text-gray-900 tracking-tight">
              {t("admin.integrations.title") || "API Keys & Technical Settings"}
            </h1>
            <p className="text-sm sm:text-base text-gray-500 mt-1 sm:mt-2">
              {t("admin.integrations.subtitle") || "Configure your providers for sending SMS, WhatsApp, and Emails."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-5 sm:p-8 space-y-8 sm:space-y-10">
            
            {/* DEMO MODE INFOS */}
            {isDemoMode && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="text-sm leading-6">
                  <span className="font-bold text-slate-900 flex items-center gap-2">
                    {t("admin.integrations.demoModeTitle")}
                  </span>
                  <p className="text-slate-700 mt-1">
                    {t("admin.integrations.demoModeDesc")}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {platformCapabilities.whatsapp && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full border border-green-200">
                        {t("admin.integrations.whatsappActive")}
                      </span>
                    )}
                    {platformCapabilities.email && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full border border-blue-200">
                        {t("admin.integrations.emailActive")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className={`space-y-10 transition-opacity ${isDemoMode ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
              
              {/* TWILIO SECTION */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 border-b border-gray-200 pb-2">
                <div className="p-2 bg-green-100 rounded-lg text-green-600">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2">
                  {t("admin.integrations.twilioHeader") || "Twilio (SMS & WhatsApp)"}
                  <button type="button" onClick={() => setShowTwilioInfo(!showTwilioInfo)} className="text-gray-400 hover:text-green-600 transition-colors">
                    <Info className="w-5 h-5" />
                  </button>
                </h2>
              </div>

              {showTwilioInfo && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg text-sm text-blue-800 mb-4 animate-fadeIn">
                  {t("admin.integrations.twilioWarning")}
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("admin.integrations.twilioAccountSid") || "Account SID"}
                  </label>
                  <input
                    type="text"
                    value={formData.twilioAccountSid}
                    onChange={(e) => setFormData({ ...formData, twilioAccountSid: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
                    placeholder="AC..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("admin.integrations.twilioAuthToken") || "Auth Token"}
                  </label>
                  <input
                    type="password"
                    value={formData.twilioAuthToken}
                    onChange={(e) => setFormData({ ...formData, twilioAuthToken: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
                    placeholder="••••••••••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("admin.integrations.twilioPhone") || "Twilio Phone (SMS)"}
                  </label>
                  <input
                    type="text"
                    value={formData.twilioPhone}
                    onChange={(e) => setFormData({ ...formData, twilioPhone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
                    placeholder="+1234567890"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("admin.integrations.twilioWhatsapp") || "Twilio Phone (WhatsApp)"}
                  </label>
                  <input
                    type="text"
                    value={formData.twilioWhatsapp}
                    onChange={(e) => setFormData({ ...formData, twilioWhatsapp: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
                    placeholder="whatsapp:+1234567890"
                  />
                </div>
              </div>
            </div>

            {/* EMAIL SECTION */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 border-b border-gray-200 pb-2">
                <div className="p-2 bg-slate-100 rounded-lg text-sky-700">
                  <Mail className="w-5 h-5" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                  {t("admin.integrations.emailHeader") || "Email Delivery"}
                </h2>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("admin.integrations.emailProvider") || "Email Provider"}
                </label>
                <select
                  value={formData.emailProvider}
                  onChange={(e) => setFormData({ ...formData, emailProvider: e.target.value })}
                  className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
                >
                  <option value="resend">Resend (API - Recommended)</option>
                  <option value="smtp">SMTP Server</option>
                </select>
              </div>

              {formData.emailProvider === "resend" && (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("admin.integrations.resendApiKey") || "Resend API Key"}
                  </label>
                  <input
                    type="password"
                    value={formData.resendApiKey}
                    onChange={(e) => setFormData({ ...formData, resendApiKey: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 font-mono text-sm"
                    placeholder="re_..."
                  />
                </div>
              )}
            </div>

            {/* SMTP SECTION (Visible only if SMTP selected or partially filled) */}
            {formData.emailProvider === "smtp" && (
              <div className="space-y-6 p-4 sm:p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3 border-b border-gray-200 pb-2">
                  <div className="p-2 bg-gray-200 rounded-lg text-gray-600">
                    <Server className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    {t("admin.integrations.smtpHeader") || "SMTP Configuration"}
                    <button type="button" onClick={() => setShowSmtpInfo(!showSmtpInfo)} className="text-gray-400 hover:text-blue-600 transition-colors">
                      <Info className="w-5 h-5" />
                    </button>
                  </h3>
                </div>

                {showSmtpInfo && (
                  <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg text-sm text-orange-800 mb-4 animate-fadeIn">
                    {t("admin.integrations.smtpWarning")}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("admin.integrations.smtpFrom") || "Sender Email (From)"}
                    </label>
                    <input
                      type="email"
                      value={formData.smtpFrom}
                      onChange={(e) => setFormData({ ...formData, smtpFrom: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                      placeholder="hello@mysalon.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("admin.integrations.smtpHost") || "SMTP Host"}
                    </label>
                    <input
                      type="text"
                      value={formData.smtpHost}
                      onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                      placeholder="smtp.mailgun.org"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("admin.integrations.smtpPort") || "SMTP Port"}
                    </label>
                    <input
                      type="number"
                      value={formData.smtpPort}
                      onChange={(e) => setFormData({ ...formData, smtpPort: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                      placeholder="465"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("admin.integrations.smtpUser") || "SMTP User"}
                    </label>
                    <input
                      type="text"
                      value={formData.smtpUser}
                      onChange={(e) => setFormData({ ...formData, smtpUser: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                      placeholder="postmaster@mysalon.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("admin.integrations.smtpPassword") || "SMTP Password"}
                    </label>
                    <input
                      type="password"
                      value={formData.smtpPassword}
                      onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                      placeholder="••••••••••••••••"
                    />
                  </div>
                </div>
              </div>
            )}
            
            </div> {/* END WRAPPER */}

            <div className="pt-4">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="w-full px-6 py-3 sm:py-4 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 font-bold text-base sm:text-lg shadow-md transition-all duration-200 uppercase tracking-wide"
              >
                {updateMutation.isPending ? t("common.loading") : t("common.save")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
      />
    </div>
  );
}
